import {WarrantyModel} from "../../../../models/domain/warrantyModel";
import * as React from "react";
import {useState} from "react";
import {Button, Card, Form, Image} from "react-bootstrap";
import {FaCheck, FaInfoCircle} from "react-icons/fa";
import {Modal} from "../warranty";
import {useWarrantyChange} from "../../../../hooks/useWarrantyChange";
import {useCartItemWarranty} from "../../../../hooks/addToCart/useCartItemWarranty";
import {useCartService} from "../../../../hooks/addToCart/useCartService";
import {useCartStatus, useWarrantyId} from "../../../../redux/stateHooks";
import {CartItemResourceType} from "../../../../api/cart";
import {GPAWebDataTypesResourceType} from "../../../../api/openApi/cartService";
import {useOffCanvasWarranty} from "../../../../hooks/addToCart/useOffCanvasWarranties";

/**
 * The WarrantDetailsProps interface defines the props for the WarrantyDetails component.
 * @param children Either the warranty options or the cart item warranty.
 * @param show Whether the warranty information is displayed.
 * @param setShow Sets whether the warranty information should be displayed.
 */
interface WarrantDetailsProps {
    children: React.ReactNode;
    show: boolean,
    setShow: (show: boolean) => void
}

/**
 * The AddToCartWarranties component is used to display the warranty options for a product.
 */
export const AddToCartWarranties = () => {
    const [show, setShow] = useState<boolean>(false);
    const model = useCartItemWarranty()
    const {warranties} = useOffCanvasWarranty()

    if (warranties.length === 0)
        return <></>;

    return (
        <WarrantyDetails setShow={setShow} show={show}>
            {model ?

                <WarrantSelectedOffer model={model} /> :
                <WarrantyOffer />

            }
        </WarrantyDetails>
    )
}

/**
 * The WarrantyDetails component is used to either display the current warranty options or the cart item warranty.
 * @param children Either the warranty options or the cart item warranty.
 * @param show Whether the warranty information is displayed.
 * @param setShow Sets whether the warranty information should be displayed.
 */
const WarrantyDetails: React.FC<WarrantDetailsProps> = ({children, show, setShow}: WarrantDetailsProps) => {
    const warranty = useCartItemWarranty()

    return <>
        <Card className="border-top">
            <Card.Body className={`${warranty && 'd-flex flex-row-reverse justify-content-between align-items-center'}`}>
            <div className="d-flex align-items-start justify-content-between">
                {!warranty ?
                    <div className="mb-intro">
                        <Button variant="link" className="p-0" onClick={() => setShow(true)}>
                            <strong>
                                Product Protection<FaInfoCircle className="ms-1" aria-hidden="true" />
                            </strong>
                        </Button>
                        <br /><small className="text-muted">*Valid in US only</small>
                    </div> : null
                }
                <div>
                    <Image src="https://static.commerceplatform.services/content/img/mulberry-warranty.jpg"
                        className="img-responsive"
                        alt="Mulberry Warranty"
                        height={22}
                        width={75} />
                </div>
            </div>
            <div className="warranty-row d-flex align-items center mt-2">
                {children}
            </div>
            <Modal show={show} onHide={() => setShow(false)}/>
            </Card.Body>
        </Card>
    </>;
}

/**
 * The WarrantyOffer component is used to display the warranty options for a product.
 */
const WarrantyOffer = () => {
    const warrantyId = useWarrantyId()
    const {request} = useCartStatus()
    const {createItemResources} = useCartService()

    const cartWarranty = useCartItemWarranty()
    const {productWarranty, warranties} = useOffCanvasWarranty(warrantyId);
    const warranty = warranties[0];

    // Note: The browser back button can leave the warrantyId for a different product in the redux state
    // We are bypassing this by checking undefined against the productWarranty because it wil only be valueful
    // if the warrantyId is for the current product.
    // This is since useOffCanvasWarranty will only return values if the warrantyId is for the current product.
    const disabled = productWarranty === undefined && warranties?.length > 1

    const AcceptOffer = async () => {
        if (request) {
            const resources: CartItemResourceType[] = []
            // Handle the case where there are multiple warranties
            if (warranties.length > 1 && !cartWarranty && productWarranty) {
                resources.push({
                    resourceType: GPAWebDataTypesResourceType.Warranty,
                    resourceId: productWarranty.id,
                    resourcePrice: productWarranty.price
                })
            }
            // Handle the case where there is only one warranty
            if (warranties.length === 1 && !cartWarranty && warranty) {
                resources.push({
                    resourceType: GPAWebDataTypesResourceType.Warranty,
                    resourceId: warranty.id,
                    resourcePrice: warranty.price
                })
            }
            await createItemResources({...request, resources: resources})
        }
    }

    if (warranties.length === 0)
        return <></>;

    return (
        <div>
            <div className="d-flex flex-row align-items-center mb-2">
                {warranties.length === 1 && <AddToCartWarrantySingleContent model={warranty}/>}
                {warranties.length > 1 && warranties.map((warranty, index) => <AddToCartWarrantyMultipleContent
                    key={index} model={warranty}/>)}
            </div>
            <div>
                <Button variant="outline-primary" className="my-2" disabled={disabled}
                        onClick={async () => await AcceptOffer()}>Add Protect Plan</Button>
            </div>
        </div>
    );
}

/**
 * The WarrantSelectedOffer component is used to display the selected cart item warranty.
 * @param title The title of the warranty.
 * @param price The cart item price of the warranty.
 * @constructor
 */
const WarrantSelectedOffer = ({model: {title, formatted: {price}}}: { model: WarrantyModel }) => {
    return (
        <div className="d-flex align-items-center">
            <FaCheck size={18} className="text-success me-1"/> <strong className="me-1">Added {title} Protection
            Plan</strong> ({price})
            <span className="warranty-selected"></span>
        </div>
    );
}

/**
 * The AddToCartWarrantySingleContent component is used to display the warranty option for a product
 * @param title The title of the warranty.
 * @param price The product price of the warranty.
 * @constructor
 */
const AddToCartWarrantySingleContent = ({model: {title, formatted: {price}}}: { model: WarrantyModel }) => {
    return (
        <div className="services-checkbox warranty-services me-2">
            {title} - {price}
            <span className="warranty-selected"></span>
        </div>);
};

/**
 * The AddToCartWarrantyMultipleContent component controls handling the user's selection of a warranty.
 * @param id The id of the warranty.
 * @param title The title of the warranty.
 * @param price The product price of the warranty.
 * @constructor
 */
const AddToCartWarrantyMultipleContent = ({model: {id, title, formatted: {price}}}: { model: WarrantyModel }) => {
    const {activeWarrantyId, onChange} = useWarrantyChange();

    return (
        <div className="services-checkbox warranty-services me-2">
            <Form.Check
                id={`servicesCheckbox-${id}`}
                type="checkbox"
                className="warranty-service-checkbox my-2"
                label={
                    <div>
                        {title} - {price}
                        <span className="warranty-selected"></span>
                    </div>
                }
                aria-label={`${title} - ${price}`}
                checked={activeWarrantyId == id}
                onChange={() => onChange(id)}
            />
        </div>);
};
